import * as PIXI from 'pixi.js';

import type { SerializedAnimationConfig } from '../../libs/AnimationCurveManager';
import { AnimationCurveManager } from '../../libs/AnimationCurveManager';
import {
    assertArray,
    assertObject
} from '../../validators';
import type { SerializableMixinSettings } from '../serializableMixin';
import { TypedSerializableMixin } from '../serializableMixin';
import { Config } from '../../video-data/config';
import type { Template } from '../template/Template';
import type { Slide } from '../slide/Slide';
import type { Subslide } from '../slide/Subslide';
import type { DestroyOptions } from '../../types';
import type Luminary from '../../';
import type { TextAsset } from '../text/TextAsset';
import { cloneDeep } from '../../libs/cloneDeep';
import type { BaseGroup } from '../BaseGroup';
import type { GraphicStyleTextAsset } from '../text/GraphicStyleTextAsset';
import type { CanvasTextAsset } from '../canvasText/asset/CanvasTextAsset';
import type { CanvasFancyCaptionAsset } from '../canvasFancyCaption/asset/CanvasFancyCaptionAsset';
import type { CanvasCaptionAsset } from '../canvasCaption/asset/CanvasCaptionAsset';
import type { BaseAsset } from '../../';
import type { CompositingAsset } from '../CompositingAsset';
import type { ColorAsset } from './ColorAsset';
import type { LottieAsset } from './LottieAsset';
import type { AssetGroup } from './AssetGroup';
import type { IconAsset } from './IconAsset';
import type { CircleAsset } from './CircleAsset';
import type { GradientAsset } from './GradientAsset';
import type { RectangleAsset } from './RectangleAsset';
import type { TimeAwareMediaAsset } from './TimeAwareMediaAsset';

export type GetAssetsOptions = {
    /**
     * do we just want to look at all children or do we want to search through all or descendants
     */
    deep: boolean;
};

export type GetAssetType<T> = T extends keyof AssetTypes ? AssetTypes[T]: AssetTypes[AssetType];

interface AssetTypes {
    [Config.CANVAS_TEXT_ASSET_TYPE]: CanvasTextAsset;
    [Config.CANVAS_FANCY_CAPTION_TYPE]: CanvasFancyCaptionAsset;
    [Config.CANVAS_CAPTION_TYPE]: CanvasCaptionAsset;
    [Config.TEXT_ASSET_TYPE]: GraphicStyleTextAsset;
    [Config.MEDIA_ASSET_TYPE]: TimeAwareMediaAsset;
    [Config.GROUP_ASSET_TYPE]: AssetGroup;
    [Config.LOTTIE_ASSET_TYPE]: LottieAsset;
    [Config.ICON_ASSET_TYPE]: IconAsset;
    [Config.COLOR_ASSET_TYPE]: ColorAsset;
    [Config.CIRCLE_ASSET_TYPE]: CircleAsset;
    [Config.GRADIENT_ASSET_TYPE]: GradientAsset;
    [Config.RECTANGLE_ASSET_TYPE]: RectangleAsset;
    [Config.COMPOSITING_ASSET_TYPE]: CompositingAsset;
    TextAsset: TextAsset;
};

type CompositingAssetName = `compositing_${number}`;
type TextAssetName = `text_${number}`;
type MediaAssetName = `media_${number}`;
type GroupAssetName = `group_${number}`;
type LottieAssetName = `lottie_${number}`;
type IconAssetName = `icon_${number}`;
type ColorAssetName = `color_${number}`;
type CircleAssetName = `circle_${number}`;
type GradientAssetName = `gradient_${number}`;
type RectangleAssetName = `rectangle_${number}`;
type WatermarkAssetName = `watermark_${number}`;
type AttributionAssetName = `attribution_${number}`;
type TextGroupAssetName = `text_group_${number}`;

export type AssetName = CompositingAssetName | TextAssetName | MediaAssetName | GroupAssetName | LottieAssetName | IconAssetName | ColorAssetName | CircleAssetName | GradientAssetName | RectangleAssetName | WatermarkAssetName | AttributionAssetName | TextGroupAssetName | string;


export type GetAssetTypeByName<T> = T extends TextAssetName ? TextAsset :
    T extends AttributionAssetName ? TextAsset :
    T extends MediaAssetName ? TimeAwareMediaAsset :
    T extends GroupAssetName ? AssetGroup :
    T extends TextGroupAssetName ? AssetGroup :
    T extends LottieAssetName ? LottieAsset :
    T extends IconAssetName ? IconAsset :
    T extends ColorAssetName ? ColorAsset :
    T extends CircleAssetName ? CircleAsset :
    T extends GradientAssetName ? GradientAsset :
    T extends RectangleAssetName ? RectangleAsset :
    T extends WatermarkAssetName ? TimeAwareMediaAsset :
    T extends CompositingAssetName ? CompositingAsset :
    Asset;


export type Asset = AssetTypes[keyof AssetTypes];

export type AssetType = keyof AssetTypes;

export type ContainerSettings = SerializableMixinSettings & {
    container: BaseGroup;
};

export class Container extends TypedSerializableMixin(PIXI.Container) {
    /**
     * The container which controls the behavior of this object
     */
    container: BaseGroup = undefined;

    /**
     * The luminary instance this object belongs to
     */
    root: Luminary = undefined;

    /**
     * The slide instance this object belongs to
     */
    slide: Slide = undefined;

    /**
     * The template instance this object uses
     */
    template: Template = undefined;

    /**
     * The class type of the object as a string
     */
    override _classType = 'Container';

    /**
     * Animation configuration store. This variable holds a list of animation curve descriptions which are being applied
     * during `applyAnimation`.
     */
    #animationConfig: SerializedAnimationConfig[] = [];

    _animationManager: AnimationCurveManager;

    constructor(settings: ContainerSettings) {
        super(settings);

        const container = settings.container;

        // reference composition
        if (container === undefined) {
            throw Error(`Container is not defined. Unable to access root.`);
        }
        this.container = container;

        // provide a direct link to root for global settings
        assertObject(container.root);
        this.root = this.container.root;

        // provide a direct link to slide for local settings
        assertObject(container.slide);
        this.slide = this.container.slide;

        this.template = this.root?.template;

        this._animationManager = new AnimationCurveManager(container);
        this._animationManager.name = this.name + '_animationManager';
    }

    /**
     * Returns the subscene if there is one, or null, if the asset is at the scene level.
     */
    get subscene(): Subslide | null {
        return this.getFirstParentOfType('Subslide') as Subslide;
    }

    /**
     * Return the parentId
     */
    get parentId(): string | null {
        // if the parent doesn't exist, return null
        if (!this.parent) {
            return null;
        }
        const assetParent = this.getParent();
        if (!assetParent) {
            return null;
        }
        // if we're parented to a subslide, return null
        if (assetParent._classType === 'Subslide') {
            return null;
        }
        // if the CaptionsManager is the parent, return null
        if (assetParent._classType === 'CaptionsManager') {
            return null;
        }
        // if we're parented to the top level (ie. Slide's workScene), return null
        if (assetParent._classType === 'BaseGroup') {
            return null;
        }

        return assetParent.id;
    }

    /**
     * Get all of the assets
     */
    getAssets(options: GetAssetsOptions = { deep: true }): Asset[] {
        return this._recursiveGetChildren([], options).filter(Config.isAsset);
    }

    /**
     * @protected
     */
    _recursiveGetChildren(children: BaseAsset[], options: GetAssetsOptions): BaseAsset[] {
        for (let i = 0; i < this._pixiContainer.children.length; i++) {
            const child = this._pixiContainer.children[i];
            if (typeof (child as any)._recursiveGetChildren !== 'function') {
                continue;
            }
            if (Config.isAsset(child)) {
                children.push(child);
            }

            if (options.deep) {
                (child as any)._recursiveGetChildren(children, options);
            }
        }
        return children;
    }

    /**
     * Get the first parent of a given class type
     * @param type - the class type of the parent we're looking for
     */
    getFirstParentOfType(type: string): BaseAsset {
        let parent = this.parent as BaseAsset;
        while (parent) {
            if (parent.classType === type) {
                return parent;
            }
            parent = parent.parent as BaseAsset;
        }
        return parent;
    }

    /**
     * Returns the first parent container that's an asset container. An asset container is container which contains
     * the interface for external use. All other containers should not be interacted with through our public API.
     */
    getParent(): BaseAsset {
        let parent = this.parent as BaseAsset;
        while (parent) {
            if (Config.isAsset(parent) || parent === this.slide?.scene || parent === this.subscene) {
                return parent as BaseAsset;
            }
            parent = parent.parent as BaseAsset;
        }
        return parent;
    }

    /**
     * Determine whether or not we have the object of the given name in our hierarchy
     * @param name - the name of the object we're searching for
     */
    hasAssetByName(name: string, options: GetAssetsOptions = { deep: true }): boolean {
        return this.getAssets(options).filter(item => item.name === name).length > 0;
    }

    /**
     * Get all assets by name by traversing our hierarchy.
     * @param name - the name of the object we're searching for
     */
    getAssetByName<T extends AssetName>(name: T, options?: GetAssetsOptions): GetAssetTypeByName<T>;
    getAssetByName(name: string, options: GetAssetsOptions = { deep: true }): Asset | undefined {
        return this.getAssets(options).find(item => item.name === name);
    }

    /**
     * Get the asset with the given UUID in our hierarchy
     */
    getAssetById(id: string, options: GetAssetsOptions = { deep: true }): Asset | undefined {
        for (const child of this.getAssets(options)) {
            if (child.id === id) {
                return child;
            }
        }
    }

    /**
     * Remove and destroy all children
     * @param childrenToSkip - a list of children we don't want to remove
     */
    clear(childrenToSkip: Container[] = []) {
        this._pixiContainer.children.filter(child => !(childrenToSkip.some(childToSkip => child as any === childToSkip)))
            .forEach(child => child.destroy(true));
    }

    /**
     * Get the classTypes of all of our descendants
     */
    getAssetTypes(options?: GetAssetsOptions): AssetType[] {
        return [...new Set(this.getAssets(options).map(item => item.classType as AssetType))];
    }

    /**
     * Get all assets of the given classType in our hierarchy.
     * @param classType - the classType of the asset we're searching for
     */
    getAssetsByType<T extends AssetType>(classType: T, options?: GetAssetsOptions): GetAssetType<T>[];
    getAssetsByType(classType: AssetType, options: GetAssetsOptions = { deep: true }): Asset[] {
        return this.getAssets(options).filter(item => item.classType === classType);
    }

    set animationManager(value: AnimationCurveManager) {
        this._animationManager = value;
    }

    /**
     * The object's AnimationCurveManager which manages the creation and evaluation of animation curves.
     */
    get animationManager(): AnimationCurveManager {
        return this._animationManager;
    }

    /**
     * The animation curve configuration data which gets used by the AnimationCurveManager during `applyCurveConfig`.
     * @param data
     */
    set animationConfig(data) {
        assertArray(data);
        this.#animationConfig = data;
    }

    /**
     * @deprecated We use dynamic animation now, but we still have some old scene designs which use raw
     * animation configuration data.
     */
    get animationConfig() {
        return this.#animationConfig;
    }

    /**
     * Returns the closest child of the given type to the given position
     * @param childType (e.g. TEXT_ASSET_TYPE, MEDIA_ASSET_TYPE)
     * @param position in screen space
     */
    getClosestChildOfTypeToPosition(childType = Config.TEXT_ASSET_TYPE, position: PIXI.Point): Container {
        let closestDistance = Infinity;
        let closestChild: Container;
        this.getAssetsByType(childType).forEach((child) => {
            const childDistanceToPoint = child.distanceToPoint(position);
            if (childDistanceToPoint < closestDistance) {
                closestDistance = childDistanceToPoint;
                closestChild = child;
            }
        });
        return closestChild;
    }

    /**
     * Returns the distance to the given position
     * @param position in screen space
     */
    distanceToPoint(position: PIXI.Point): number {
        const bounds = this.getBounds();
        const min = new PIXI.Point(bounds.x, bounds.y);
        const max = new PIXI.Point(bounds.x + bounds.width, bounds.y + bounds.height);
        const dx = Math.max(min.x - position.x, 0, position.x - max.x);
        const dy = Math.max(min.y - position.y, 0, position.y - max.y);
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Removes all animation by deleting all animation curves created by the `AnimationCurveManager`.
     */
    clearAnimation() {
        if (!this.animationManager) {
            return;
        }
        this.animationManager.clear();
    }

    /**
     * Evaluates all animation curves at the given progress value.
     * @param percentage - A value between 0 and 1
     */
    evalAnimationAtPercentage(percentage: number) {
        this.animationManager.percentage = percentage;
    }

    /**
     * Applies the animation configuration specified in `animationConfig`.
     * @deprecated We use dynamic animation now, but we still have some old scene designs which use this
     */
    applyAnimation() {
        if (!this.animationManager) {
            return;
        }
        if (!this.animationConfig) {
            return;
        }
        // create the individual animation curves
        this.animationConfig.forEach(config => this.#resolveDescriptionAndCreateCurve(config));
    }

    /**
     * Create an individual animation curve which drives one of more properties. This happens in 2 stages:
     * - resolve any property references and update curve description
     * - create the animation curve
     */
    #resolveDescriptionAndCreateCurve(config: SerializedAnimationConfig) {
        if (typeof config !== 'object') {
            return;
        }
        // clone the object so we don't loose our original references
        const localConfig = cloneDeep(config);

        // Some curve descriptions require some of their range values to be calculated based on the current setup which
        // is what we resolve here.
        const drivenRange = localConfig.drivenRange;
        if (typeof drivenRange[0] === 'string') {
            drivenRange[0] = this._resolvePropertyReference(drivenRange[0]);
        }
        else if (typeof drivenRange[0] === 'object') {
            drivenRange[0] = this._resolveObjectProperty(drivenRange[0]);
        }
        if (typeof drivenRange[1] === 'string') {
            drivenRange[1] = this._resolvePropertyReference(drivenRange[1]);
        }
        else if (typeof drivenRange[1] === 'object') {
            drivenRange[1] = this._resolveObjectProperty(drivenRange[1]);
        }

        // create the actual animation
        this.animationManager.add(localConfig, {
            drivenObjects: [this],
            duration: this.slide.duration,
            drivenRange,
        });
    }

    _resolveObjectProperty(objectProperty): number {
        // validate data
        if (Object.keys(objectProperty).length > 1) {
            throw Error('Invalid range specified.  Should be {"property":value}');
        }
        const key = Object.keys(objectProperty)[0];
        let result = 0;
        switch (key) {
            case 'width':
                result = this.root.referenceResolution.width * objectProperty['width'];
                break;
            case 'height':
                result = this.root.referenceResolution.height * objectProperty['height'];
                break;
            default:
                throw new Error('Could not handle property: ' + objectProperty[key]);
        }
        return result;
    }

    _resolvePropertyReference(propertyReference): number {
        // extract multiplier
        let multiplier = 1;
        if (propertyReference.startsWith('-')) {
            multiplier = -1;
            propertyReference = propertyReference.slice(1, propertyReference.length);
        }

        // resolve reference
        let result = 0;
        switch (propertyReference) {
            // WIDTH
            case 'slide_width':
                result = this.root.referenceResolution.width;
                break;
            case 'object_width': // TODO: not in use, can we remove?
                result = this.width;
                break;
            case 'mask_width':
                if (!(this.mask instanceof PIXI.Container)) {
                    throw Error(`Object ${this.name} does't have a mask. Unable to retrieve height (${this.mask}).`);
                }
                result = this.mask.width;
                break;
            case 'object_width_minus_mask_width': // TODO: not in use, can we remove?
                if (!(this.mask instanceof PIXI.Container)) {
                    throw Error(`Object ${this.name} does't have a mask. Unable to retrieve height (${this.mask}).`);
                }
                result = this.width - this.mask.width;
                break;
            case 'slide_width_minus_mask_width': // TODO: not in use, can we remove?
                if (!(this.mask instanceof PIXI.Container)) {
                    throw Error(`Object ${this.name} does't have a mask. Unable to retrieve height (${this.mask}).`);
                }
                result = this.root.referenceResolution.width - this.mask.width;
                break;
            // HEIGHT
            case 'slide_height':
                result = this.root.referenceResolution.height;
                break;
            case 'object_height': // TODO: not in use, can we remove?
                result = this.height;
                break;
            case 'mask_height':
                if (!(this.mask instanceof PIXI.Container)) {
                    throw Error(`Object ${this.name} does't have a mask. Unable to retrieve height.`);
                }
                result = this.mask.height;
                break;
            case 'slide_height_minus_mask_height': // TODO: not in use, can we remove?
                if (!(this.mask instanceof PIXI.Container)) {
                    throw Error(`Object ${this.name} does't have a mask. Unable to retrieve height.`);
                }
                result = this.root.referenceResolution.height - this.mask.height;
                break;
        }

        if (Number.isNaN(result)) {
            throw Error(`PropertyReference for ${propertyReference} is NaN`);
        }

        return result * multiplier;
    }

    /**
     * @internal
     */
    get _pixiContainer(): PIXI.Container {
        return this as any as PIXI.Container;
    }

    _addInternalChild(child: PIXI.DisplayObject) {
        return this._pixiContainer.addChild(child);
    }

    _addInternalChildAt(child: PIXI.DisplayObject, index: number) {
        return this._pixiContainer.addChildAt(child, index);
    }

    _removeInternalChild(child: PIXI.DisplayObject) {
        return this._pixiContainer.removeChild(child);
    }

    /**
     * Removes all internal references and listeners as well as removes children from the display list.
     * Do not use a Container after calling `destroy`.
     */
    override destroy(options: DestroyOptions) {
        if (this._animationManager) {
            this._animationManager.destroy();
            this._animationManager = null;
        }
        super.destroy(options);
        this.container = null;
        this.root = null;
        this.slide = null;
    }

    /**
     * @inheritDoc
     */
    override get readonlyProperties() {
        return super.readonlyProperties.concat([
            'parentId',
        ]);
    }

    /**
     * @inheritDoc
     */
    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'animationConfig',
        ]);
    }
}
